import React, { useState, useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue, useFieldArray } from 'react-hook-form';
import { PlusCircleIcon, TrashIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData, PeriodicExam } from '@/features/Collaborators/Edit/Types';
import { FileUploadInput } from '@/components/shared';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';

interface PeriodicExamsFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
}

const examTypeOptions: PeriodicExam['examType'][] = ['Periódico', 'Mudança de Função', 'Retorno ao Trabalho'];
const resultOptions: PeriodicExam['result'][] = ['Apto', 'Inapto', 'Apto com restrições'];

const PeriodicExamsFieldset: React.FC<PeriodicExamsFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors,
    isSubmitting = false
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Estado local para os dados do formulário (necessário para o useFileUpload)
    const [formData, setFormData] = useState<CollaboratorFormData>({} as CollaboratorFormData);

    // File upload hook
    const { uploadStates, handleFileChange } = useFileUpload(setFormData);

    // Sincronizar mudanças do React Hook Form com o estado local
    useEffect(() => {
        const subscription = watch((value) => {
            setFormData(value as CollaboratorFormData);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // Helper para calcular próxima data do exame
    const calculateNextExamDate = (examDate: string): string => {
        if (!examDate) return '';
        const currentDate = new Date(examDate);
        currentDate.setFullYear(currentDate.getFullYear() + 2);
        return currentDate.toISOString().split('T')[0];
    };

    // Helper para determinar status do exame
    const getExamStatus = (nextExamDate: string) => {
        if (!nextExamDate) return 'unknown';
        
        const today = new Date();
        const nextDate = new Date(nextExamDate);
        const diffTime = nextDate.getTime() - today.getTime();
        const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));

        if (diffMonths < 0) return 'expired';
        if (diffMonths <= 6) return 'warning';
        return 'valid';
    };

    // Helper para obter classe CSS do status
    const getStatusClasses = (status: string) => {
        switch (status) {
            case 'expired': return 'border-red-200 bg-red-50';
            case 'warning': return 'border-yellow-200 bg-yellow-50';
            case 'valid': return 'border-green-200 bg-green-50';
            default: return 'border-gray-200 bg-white';
        }
    };

    // Helper para obter ícone do status
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'expired': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
            case 'warning': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
            case 'valid': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            default: return null;
        }
    };

    // Helper local para obter layout contextual
    const getLayoutByContext = (context: CollaboratorFormContext) => {
        switch (context) {
            case 'DP_UPDATE':
            case 'DHO_UPDATE':
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
            default: // FULL_FORM
                return {
                    fieldsetStyle: 'border p-4 rounded-md',
                    legendStyle: 'text-sm font-medium px-1 text-gray-400'
                };
        }
    };

    const layout = getLayoutByContext(context);

    // useFieldArray para gerenciar exames periódicos
    const { fields, append, remove } = useFieldArray({
        control,
        name: "periodicExams"
    });

    // Verificar se o fieldset deve ser exibido
    if (!shouldShow('periodicExams')) {
        return null;
    }

    const handleFileUpload = (index: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Upload do arquivo e atualização do URL no React Hook Form
        try {
            const downloadURL = await handleFileChange(e, {
                fieldName: `periodicExams[${index}].examFile`,
                urlFieldName: `periodicExams[${index}].examUrl`,
                pathPrefix: 'periodic-exams',
                id: collaboratorId
            });
            
            // Atualizar React Hook Form com a URL
            if (downloadURL) {
                setValue(`periodicExams.${index}.examUrl`, downloadURL);
            }
        } catch (error) {
            console.error(`Erro no upload do exame periódico ${index}:`, error);
        }
    };

    const handleAddExam = () => {
        const today = new Date().toISOString().split('T')[0];
        const nextExam = calculateNextExamDate(today);
        
        append({
            id: '',
            examDate: today,
            examType: 'Periódico',
            nextExamDate: nextExam,
            doctorName: '',
            clinicName: '',
            result: 'Apto',
            restrictions: '',
            examFile: undefined,
            examUrl: ''
        });
    };

    const handleExamDateChange = (index: number, newDate: string) => {
        const nextExamDate = calculateNextExamDate(newDate);
        setValue(`periodicExams.${index}.nextExamDate`, nextExamDate);
    };

    return (
        <fieldset className={layout.fieldsetStyle}>
            <legend className={layout.legendStyle}>Exames Periódicos</legend>
            <div className="space-y-4 pt-2">
                {fields.map((field, index) => {
                    const exam = watch(`periodicExams.${index}`);
                    const status = getExamStatus(exam?.nextExamDate);
                    
                    return (
                        <div key={field.id} className={`border rounded-lg p-4 ${getStatusClasses(status)}`}>
                            {/* Header com status */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    {getStatusIcon(status)}
                                    <h4 className="text-sm font-medium text-gray-700">
                                        Exame #{index + 1}
                                    </h4>
                                    {status === 'expired' && (
                                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Vencido</span>
                                    )}
                                    {status === 'warning' && (
                                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Próximo do vencimento</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Grid principal com campos */}
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                {/* Data do Exame */}
                                <div className="md:col-span-2">
                                    <label htmlFor={`exam-date-${index}`} className="block text-sm font-medium text-gray-700">
                                        Data do Exame *
                                    </label>
                                    <Controller
                                        name={`periodicExams.${index}.examDate`}
                                        control={control}
                                        render={({ field: dateField, fieldState }) => (
                                            <>
                                                <input
                                                    {...dateField}
                                                    type="date"
                                                    id={`exam-date-${index}`}
                                                    className="input-primary w-full mt-1"
                                                    disabled={isSubmitting}
                                                    onChange={(e) => {
                                                        dateField.onChange(e);
                                                        handleExamDateChange(index, e.target.value);
                                                    }}
                                                />
                                                {fieldState.error && (
                                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>

                                {/* Tipo do Exame */}
                                <div className="md:col-span-2">
                                    <label htmlFor={`exam-type-${index}`} className="block text-sm font-medium text-gray-700">
                                        Tipo *
                                    </label>
                                    <Controller
                                        name={`periodicExams.${index}.examType`}
                                        control={control}
                                        render={({ field: typeField, fieldState }) => (
                                            <>
                                                <select
                                                    {...typeField}
                                                    id={`exam-type-${index}`}
                                                    className="input-primary w-full mt-1"
                                                    disabled={isSubmitting}
                                                >
                                                    {examTypeOptions.map(option => (
                                                        <option key={option} value={option}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                                {fieldState.error && (
                                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>

                                {/* Resultado */}
                                <div className="md:col-span-2">
                                    <label htmlFor={`exam-result-${index}`} className="block text-sm font-medium text-gray-700">
                                        Resultado *
                                    </label>
                                    <Controller
                                        name={`periodicExams.${index}.result`}
                                        control={control}
                                        render={({ field: resultField, fieldState }) => (
                                            <>
                                                <select
                                                    {...resultField}
                                                    id={`exam-result-${index}`}
                                                    className="input-primary w-full mt-1"
                                                    disabled={isSubmitting}
                                                >
                                                    {resultOptions.map(option => (
                                                        <option key={option} value={option}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                                {fieldState.error && (
                                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>

                                {/* Próximo Exame */}
                                <div className="md:col-span-2">
                                    <label htmlFor={`next-exam-${index}`} className="block text-sm font-medium text-gray-700">
                                        Próximo Exame
                                    </label>
                                    <Controller
                                        name={`periodicExams.${index}.nextExamDate`}
                                        control={control}
                                        render={({ field: nextField, fieldState }) => (
                                            <>
                                                <input
                                                    {...nextField}
                                                    type="date"
                                                    id={`next-exam-${index}`}
                                                    className="input-primary w-full mt-1 bg-gray-50"
                                                    disabled={true}
                                                />
                                                {fieldState.error && (
                                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>

                                {/* Upload de Arquivo */}
                                <div className="md:col-span-4">
                                    <Controller
                                        name={`periodicExams.${index}.examFile`}
                                        control={control}
                                        render={({ field: fileField, fieldState }) => (
                                            <div>
                                                <FileUploadInput
                                                    name={`examFile-${index}`}
                                                    label="Anexar Exame"
                                                    fileUrl={watch(`periodicExams.${index}.examUrl`) || ''}
                                                    fileName={fileField.value?.name || ''}
                                                    isSubmitting={isSubmitting}
                                                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            fileField.onChange(file);
                                                            await handleFileUpload(index)(e);
                                                        }
                                                    }}
                                                    progress={uploadStates[`periodicExams[${index}].examFile`]?.progress}
                                                    status={uploadStates[`periodicExams[${index}].examFile`]?.status || 'idle'}
                                                />
                                                {fieldState.error && (
                                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Segunda linha - Médico e Clínica */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                {/* Nome do Médico */}
                                <div>
                                    <label htmlFor={`doctor-${index}`} className="block text-sm font-medium text-gray-700">
                                        Nome do Médico
                                    </label>
                                    <Controller
                                        name={`periodicExams.${index}.doctorName`}
                                        control={control}
                                        render={({ field: doctorField, fieldState }) => (
                                            <>
                                                <input
                                                    {...doctorField}
                                                    type="text"
                                                    id={`doctor-${index}`}
                                                    className="input-primary w-full mt-1"
                                                    disabled={isSubmitting}
                                                />
                                                {fieldState.error && (
                                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>

                                {/* Nome da Clínica */}
                                <div>
                                    <label htmlFor={`clinic-${index}`} className="block text-sm font-medium text-gray-700">
                                        Nome da Clínica
                                    </label>
                                    <Controller
                                        name={`periodicExams.${index}.clinicName`}
                                        control={control}
                                        render={({ field: clinicField, fieldState }) => (
                                            <>
                                                <input
                                                    {...clinicField}
                                                    type="text"
                                                    id={`clinic-${index}`}
                                                    className="input-primary w-full mt-1"
                                                    disabled={isSubmitting}
                                                />
                                                {fieldState.error && (
                                                    <p className="mt-1 text-sm text-red-600" role="alert">
                                                        {fieldState.error.message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Terceira linha - Observações/Restrições */}
                            <div className="mt-4">
                                <label htmlFor={`restrictions-${index}`} className="block text-sm font-medium text-gray-700">
                                    Observações/Restrições
                                </label>
                                <Controller
                                    name={`periodicExams.${index}.restrictions`}
                                    control={control}
                                    render={({ field: restrictionsField, fieldState }) => (
                                        <>
                                            <textarea
                                                {...restrictionsField}
                                                id={`restrictions-${index}`}
                                                rows={2}
                                                className="input-primary w-full mt-1"
                                                disabled={isSubmitting}
                                                placeholder="Observações sobre o exame ou restrições aplicáveis..."
                                            />
                                            {fieldState.error && (
                                                <p className="mt-1 text-sm text-red-600" role="alert">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </>
                                    )}
                                />
                            </div>

                            {/* Controller oculto para examUrl */}
                            <Controller
                                name={`periodicExams.${index}.examUrl`}
                                control={control}
                                render={({ field }) => (
                                    <input type="hidden" {...field} value={field.value || ''} />
                                )}
                            />
                        </div>
                    );
                })}

                <div className="flex justify-start">
                    <button
                        type="button"
                        onClick={handleAddExam}
                        className="flex items-center gap-2 px-4 py-2 text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50 transition-colors"
                        disabled={isSubmitting}
                    >
                        <PlusCircleIcon className="h-5 w-5 mr-2" />
                        Adicionar Exame Periódico
                    </button>
                </div>

                {errors.periodicExams && (
                    <p className="mt-2 text-sm text-red-600" role="alert">
                        {errors.periodicExams.message}
                    </p>
                )}
            </div>
        </fieldset>
    );
};

export default PeriodicExamsFieldset;